import React from 'react';
import {CreditCardIcon, PencilSquareIcon, TrashIcon} from '@heroicons/react/24/outline';
import type {PaymentCardType} from 'api/generated';
import {Dropdown} from 'components';

interface IPaymentCardItemProps {
  card: PaymentCardType;
  onEdit: (card: PaymentCardType) => void;
  onSetDefault: (card: PaymentCardType) => void;
  onDelete: (card: PaymentCardType) => void;
}

const PaymentCardItem = ({card, onEdit, onSetDefault, onDelete}: IPaymentCardItemProps) => {
  const dropdownItems = [
    [
      {icon: PencilSquareIcon, text: 'Edit', onClick: () => onEdit(card)},
      ...(card.default ? [] : [{icon: CreditCardIcon, text: 'Set default', onClick: () => onSetDefault(card)}]),
    ],
    [{icon: TrashIcon, text: 'Delete', onClick: () => onDelete(card), color: 'text-red-500'}],
  ];

  const billingAddress = card.billingAddress;
  return (
    <div className="relative grid grid-cols-1 sm:grid-cols-7 p-4 gap-4 sm:divide-x-1 divide-slate-400 dark:divide-slate-600 border border-slate-200 dark:border-slate-500 shadow-sm rounded-md bg-white dark:bg-slate-900">
      <div className="col-span-1 sm:col-span-2 text-sm sm:min-h-28">
        <div className="flex items-center pb-1 gap-1.5">
          <CreditCardIcon className="size-4.5 dark:text-white" />
          <h2 className="font-bold dark:text-white">Payment Card</h2>
        </div>
        <p className="capitalize font-semibold">
          {card.brand} **** {card.ending}
        </p>
        <p>
          Expires on {card.expMonth.toString().padStart(2, '0')}/{card.expYear}
        </p>
        {card.default && (
          <div className="-mx-1 mt-2.5 hidden sm:block">
            <p className="inline-flex text-primary border border-primary text-xs font-bold py-1 px-3 rounded-md">Default</p>
          </div>
        )}
      </div>
      <div className="col-span-1 sm:col-span-4 text-sm">
        <h2 className="pb-1 font-bold dark:text-white">Billing address</h2>
        <div className="italic">
          <p>{billingAddress?.name}</p>
          <p>{billingAddress?.line1}</p>
          <p>{billingAddress?.line2}</p>
          <p>
            {billingAddress?.city}{' '}
            {billingAddress?.state || billingAddress?.postalCode ? `, ${billingAddress?.state} ${billingAddress?.postalCode}` : ''}
          </p>
          <p>{billingAddress?.country}</p>
        </div>
      </div>
      <div className="absolute top-2 right-2 sm:relative sm:top-0 sm:right-0 col-span-1 flex justify-end sm:justify-center items-center gap-2">
        {card.default && <p className="inline-flex text-primary border border-primary text-xs font-bold py-1 px-3 rounded-md sm:hidden">Default</p>}
        <Dropdown items={dropdownItems} />
      </div>
    </div>
  );
};

export default React.memo(PaymentCardItem);
